import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboardingState } from '@/Pages/Onboarding/hooks/useOnboardingState.jsx';
import { onboardingV2Api } from '@/data/api/onboardingV2';
import { useAuthService } from '@/hook/useAuthService';
import { MONTHLY_PLANS, YEARLY_PLANS } from '@/data/utils/plansData';
import Logo from '@/Asset/images/logo-blaxk.webp';
import './CreateProfile.css';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardBackspaceOutlinedIcon from '@mui/icons-material/KeyboardBackspaceOutlined';
import profile from '@/Asset/images/create-profile.webp';
import Toast from '@/utils/toastManager';
import EastIcon from '@mui/icons-material/East';
import UserExistsModal from './UserExistsModal';
import { trackFirstPromoterReferral } from '@/utils/firstPromoter';
import { loadRecaptchaScript, executeRecaptcha } from '@/utils/recaptcha';

const CreateProfile = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { loggedInUser } = useAuthService();
	const { updateState } = useOnboardingState();
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [formError, setFormError] = useState('');
	const [loading, setLoading] = useState(false);
	const [showUserExistsModal, setShowUserExistsModal] = useState(false);
	const hasTrackedStartRef = useRef(false);
	const [formData, setFormData] = useState({
		firstName: '',
		lastName: '',
		email: '',
		password: '',
		confirmPassword: '',
		agreedToPolicy: false,
		userType: 'individual'
	});

	useEffect(() => {
		loadRecaptchaScript().catch(() => {});
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const planId = params.get('planId');
		const plan = params.get('plan');
		const billing = params.get('billing');

		if (planId && plan && billing) {
			// Find the cbItem from plansData
			const allPlans = [...MONTHLY_PLANS, ...YEARLY_PLANS];
			let cbItem = null;

			for (const planData of allPlans) {
				if (planData.id === planId) {
					cbItem = planData.cbItem;
					break;
				}
				if (planData.hasSeatSelection && planData.seats) {
					const seat = planData.seats.find(s => s.seatId === planId);
					if (seat) {
						cbItem = seat.cbItem;
						break;
					}
				}
			}

			persistOnboardingState({
				selectedPlanId: planId,
				selectedPlanType: plan,
				selectedBillingCycle: billing,
				selectedPlanCbItem: cbItem
			});
		}
	}, [location.search]);

	const persistOnboardingState = updates => {
		updateState(updates);
		const currentState = JSON.parse(sessionStorage.getItem('onboarding_state') || '{}');
		sessionStorage.setItem(
			'onboarding_state',
			JSON.stringify({
				...currentState,
				...updates
			})
		);
	};

	const handleFormStartTrigger = () => {
		if (hasTrackedStartRef.current) return;

		window?.fbq?.('trackCustom', 'OnboardingV2_Started', {
			CustomEvent: 'OnboardingV2_Started'
		});
		hasTrackedStartRef.current = true;
	};

	const handleChange = (field, value) => {
		const nextValue = field === 'email' ? value.toLowerCase() : value;
		handleFormStartTrigger();
		setFormData(prev => ({
			...prev,
			[field]: nextValue
		}));
	};

	const showAppleLogin = useMemo(() => {
		const ua = navigator.userAgent;
		const isAppleDevice = /iPad|iPhone|iPod|Macintosh/.test(ua);
		const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|Edg/.test(ua);
		return isAppleDevice && isSafari;
	}, []);

	const handleSocialLogin = async (provider) => {
		setLoading(true);
		setFormError('');
		try {
			const result = await signInWithPopup(auth, provider);
			const user = result.user;
			const idToken = await user.getIdToken();
			const response = await api.socialLoginApi(idToken);
			dispatch(login(response?.data));
			
			console.log('[CreateProfile Social] Profile logged in, proceeding...');
			trackFirstPromoterReferral({
				email: response.data?.email,
				uid: response.data?.userId || response.data?._id
			});
			await loggedInUser(response?.data, false, { skipRedirect: true });

			persistOnboardingState({
				profileCreated: true,
				userType: 'individual',
				currentStep: 1,
				profileData: {
					firstName: response.data?.firstname || '',
					lastName: response.data?.lastname || '',
					email: response.data?.email || ''
				}
			});

			window?.fbq?.('trackCustom', 'OnboardingV2_ProfileCreated', {
				CustomEvent: 'OnboardingV2_ProfileCreated'
			});

			Toast.success('Logged in successfully!');

			const params = new URLSearchParams(location.search);
			const planId = params.get('planId');
			const plan = params.get('plan');
			const billing = params.get('billing');

			if (planId && plan && billing) {
				navigate(`/onboarding/checkout${location.search}`);
			} else {
				navigate(`/onboarding/select-plan${location.search}`);
			}
		} catch (error) {
			if (isFirebaseSilentError(error)) return;
			setFormError(getSocialAuthErrorMessage(error, 'Social login failed. Please try again.'));
		} finally {
			setLoading(false);
		}
	};
	const handleSubmit = async e => {
		e.preventDefault();
		setFormError('');

		if (formData.password !== formData.confirmPassword) {
			setFormError('Password and confirm password must match.');
			return;
		}

		if (formData.password.length < 6) {
			setFormError('Password must be at least 6 characters.');
			return;
		}

		setLoading(true);

		try {
			// Execute Google reCAPTCHA v3 verification
			let recaptchaToken = '';
			try {
				recaptchaToken = await executeRecaptcha('signup');
			} catch (recaptchaErr) {
				setFormError(
					recaptchaErr.message ||
						'Security verification failed. If you are using an ad blocker or privacy extension, please disable it for this page and try again.'
				);
				setLoading(false);
				return;
			}

			const isRecaptchaConfigured = Boolean(import.meta.env.VITE_RECAPTCHA_SITE_KEY);
			if (isRecaptchaConfigured && !recaptchaToken) {
				setFormError(
					'Security verification could not be completed. If you are using an ad blocker or privacy extension, please disable it for this page and try again.'
				);
				setLoading(false);
				return;
			}

			const response = await onboardingV2Api.createProfile({
				firstname: formData.firstName,
				lastname: formData.lastName,
				email: formData.email.toLowerCase(),
				password: formData.password,
				recaptchaToken
			});

			if (response.status === 200) {
				trackFirstPromoterReferral({
					email: response.data?.email || formData.email,
					uid: response.data?.userId || response.data?._id
				});
				await loggedInUser(response.data, false, { skipRedirect: true });

				persistOnboardingState({
					profileCreated: true,
					userType: formData.userType,
					currentStep: 1,
					profileData: {
						firstName: formData.firstName,
						lastName: formData.lastName,
						email: formData.email.toLowerCase()
					}
				});

				window?.fbq?.('trackCustom', 'OnboardingV2_ProfileCreated', {
					CustomEvent: 'OnboardingV2_ProfileCreated'
				});

				Toast.success('Profile created successfully!');

				const params = new URLSearchParams(location.search);
				const planId = params.get('planId');
				const plan = params.get('plan');
				const billing = params.get('billing');

				if (planId && plan && billing) {
					navigate(`/onboarding/checkout${location.search}`);
				} else {
					navigate(`/onboarding/select-plan${location.search}`);
				}
			}
		} catch (error) {
			if (error.response) {
				if (error.response.data.user) {
					setShowUserExistsModal(true);
				} else {
					setFormError(error.response.data.message || 'Failed to create profile');
				}
			} else {
				setFormError('Network error. Please try again.');
			}
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<UserExistsModal isOpen={showUserExistsModal} onClose={() => setShowUserExistsModal(false)} />
			<div className="onboarding-create-profile">
				<div className="onboarding-create-profile__grid-overlay" />

				<header className="onboarding-create-profile__header">
					<div className="onboarding-create-profile__brand-row">
						<button type="button" className="onboarding-create-profile__back-btn" onClick={() => navigate('/login')}>
							<KeyboardBackspaceOutlinedIcon />
						</button>
						<img src={Logo} alt="The Godfrey Method On Demand" className="onboarding-create-profile__logo" />
					</div>
					<h1 className="onboarding-create-profile__title">Create Your Profile</h1>
				</header>

				<section className="onboarding-create-profile__content">
					<div className="onboarding-create-profile__visual" aria-hidden="true">
						<img src={profile} alt="App preview" className="onboarding-create-profile__phone-shot" />
					</div>

					<form className="onboarding-create-profile__form" onSubmit={handleSubmit}>
						<label className="onboarding-create-profile__label" htmlFor="firstName">
							First Name
						</label>
						<div className="onboarding-create-profile__input-with-icon">
							<PersonOutlineOutlinedIcon className="onboarding-create-profile__input-icon" />
							<input
								id="firstName"
								className="onboarding-create-profile__input onboarding-create-profile__input--with-icon"
								type="text"
								placeholder="Jane"
								value={formData.firstName}
								onChange={e => handleChange('firstName', e.target.value)}
								required
							/>
						</div>

						<label className="onboarding-create-profile__label" htmlFor="lastName">
							Last Name
						</label>
						<div className="onboarding-create-profile__input-with-icon">
							<PersonOutlineOutlinedIcon className="onboarding-create-profile__input-icon" />
							<input
								id="lastName"
								className="onboarding-create-profile__input onboarding-create-profile__input--with-icon"
								type="text"
								placeholder="Doe"
								value={formData.lastName}
								onChange={e => handleChange('lastName', e.target.value)}
								required
							/>
						</div>

						<label className="onboarding-create-profile__label" htmlFor="email">
							Email
						</label>
						<div className="onboarding-create-profile__input-with-icon">
							<EmailOutlinedIcon className="onboarding-create-profile__input-icon" />
							<input
								id="email"
								className="onboarding-create-profile__input onboarding-create-profile__input--with-icon"
								type="email"
								placeholder="jane@example.com"
								value={formData.email}
								onChange={e => handleChange('email', e.target.value)}
								required
							/>
						</div>

						<div className="onboarding-create-profile__password-grid">
							<div>
								<label className="onboarding-create-profile__label" htmlFor="password">
									Password
								</label>
								<div className="onboarding-create-profile__password-wrap">
									<input
										id="password"
										className="onboarding-create-profile__input"
										type={showPassword ? 'text' : 'password'}
										placeholder="********"
										value={formData.password}
										onChange={e => handleChange('password', e.target.value)}
										required
									/>
									<button
										type="button"
										className="onboarding-create-profile__password-toggle"
										onClick={() => setShowPassword(prev => !prev)}
									>
										{showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
									</button>
								</div>
							</div>

							<div>
								<label className="onboarding-create-profile__label" htmlFor="confirmPassword">
									Confirm Password
								</label>
								<div className="onboarding-create-profile__password-wrap">
									<input
										id="confirmPassword"
										className="onboarding-create-profile__input"
										type={showConfirmPassword ? 'text' : 'password'}
										placeholder="********"
										value={formData.confirmPassword}
										onChange={e => handleChange('confirmPassword', e.target.value)}
										required
									/>
									<button
										type="button"
										className="onboarding-create-profile__password-toggle"
										onClick={() => setShowConfirmPassword(prev => !prev)}
									>
										{showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
									</button>
								</div>
							</div>
						</div>

						<label className="onboarding-create-profile__terms">
							<input
								type="checkbox"
								checked={formData.agreedToPolicy}
								onChange={e => handleChange('agreedToPolicy', e.target.checked)}
								required
							/>
							<span>
								I agree to the{' '}
								<a target="blank" href="https://thegodfreymethod.com/pages/terms-of-service" className="service">
									Terms of Service
								</a>
								,{' '}
								<a target="blank" href="https://thegodfreymethod.com/pages/subscription-terms" className="service">
									Subscription Terms
								</a>{' '}
								and{' '}
								<a href="https://thegodfreymethod.com/pages/privacy-policy" className="privacy">
									Privacy Policy
								</a>
								.
							</span>
						</label>

						{formError ? <p className="onboarding-create-profile__error">{formError}</p> : null}

						<div className="onboarding-create-profile__actions">
							<button type="submit" className="onboarding-create-profile__cta" disabled={loading}>
								{loading ? (
									'Creating Profile...'
								) : (
									<span>
										{' '}
										Select Your Plan <EastIcon />
									</span>
								)}
							</button>
						</div>
					</form>
				</section>
			</div>
		</>
	);
};

export default CreateProfile;
